import React, { useMemo, useState } from 'react'
import classNames from 'classnames/bind'
import { motion, AnimatePresence } from 'framer-motion'
import { graphql, useStaticQuery } from 'gatsby'
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews'
import { PieChart } from 'react-minimal-pie-chart'
import { useLocation } from '@reach/router'

import { CircularCaption, MonumentHeading2 } from 'components/ui/Text'
import { TooltipBox } from 'components/ui/Tooltip'
import useMaximumVotes from 'lib/useMaximumVotes'
import useIsDesktop from 'lib/useIsDesktop'
import useFirebaseContext from 'context/firebase'
import useUIContext from 'context/ui'
import useCopyContext from 'context/copy'
import getLocalizedData from 'lib/getLocalizedData'

import populateData from './populateData'
import { tooltipVariants } from './variants'
import * as s from './Counter.module.scss'
const cn = classNames.bind(s)
import { CounterProps } from './CounterTypes'

const counterQuery = graphql`
  query counterQuery {
    allPrismicSolutionsPage {
      edges {
        node {
          _previewable
          lang
          data {
            votes_earned_explanation {
              raw
            }
            votes_remaining_explanation {
              raw
            }
          }
        }
      }
    }
  }
`

const Counter = ({ title, className, smallOnMobile }: CounterProps) => {
  const staticData = useStaticQuery(counterQuery)
  const { data } = useMergePrismicPreviewData(staticData)
  const { pathname } = useLocation()
  const copy = useCopyContext(s => s.copy)

  const prismicSolutionsPage = getLocalizedData(pathname, data.allPrismicSolutionsPage)

  const {
    data: { votes_earned_explanation, votes_remaining_explanation },
  } = prismicSolutionsPage

  const isDesktop = useIsDesktop()

  /* Firebase and vote data */
  const max = useMaximumVotes()
  const spent = useFirebaseContext(s => s.spentVotes)
  const earned = useFirebaseContext(s => s.earnedVotes)
  const remaining = useMemo(() => (earned || 0) - (spent || 0), [earned, spent])
  const earnedPercentage = useMemo(() => ((earned || 0) / max) * 100, [earned, max])
  const remainingPercentage = useMemo(() => ((remaining || 0) / max) * 100, [remaining, max])
  /* Only user interactions should trigger the counter to animate (not Firebase events) */
  const { animate, votingPossible } = useUIContext(s => s.votesAnimation)
  const triggerVotesAnimation = useUIContext(s => s.triggerVotesAnimation)

  const paths = useMemo(
    () => [
      { type: 'full', stroke: '100 100', initial: false },
      {
        type: 'earned',
        stroke: `${earnedPercentage} 100`,
        initial: { opacity: 0 },
        description: populateData(votes_earned_explanation.raw, earned, remaining, max),
      },
      {
        type: 'remaining',
        stroke: `${remainingPercentage} 100`,
        initial: { strokeDasharray: '0 100' },
        description: populateData(votes_remaining_explanation.raw, earned, remaining, max),
      },
    ],
    [
      earnedPercentage,
      remainingPercentage,
      votes_earned_explanation,
      votes_remaining_explanation,
      earned,
      max,
      remaining,
    ],
  )

  /* Data used in pie chart that handles the mouse and click events */
  const pieChartData = useMemo(
    () => [
      { type: 'remaining', value: remaining, color: 'transparent' },
      { type: 'earned', value: (earned || 0) - remaining, color: 'transparent' },
      { type: 'full', value: max - (earned || 0), color: 'transparent' },
    ],
    [remaining, max, earned],
  )

  const hide = earned === undefined && spent === undefined && remaining !== 0
  const count = remaining < 10 ? `0${remaining}` : `${remaining}`
  const parts = count.split('')

  /* Tooltip */
  const [active, setActive] = useState<string | null>('')
  const activeTooltip = paths.find(p => p.type === active)?.description
  const renderTooltip = isDesktop || (!isDesktop && !smallOnMobile)

  return (
    <motion.div
      transition={{ duration: 0.6, type: 'spring' }}
      animate={animate && votingPossible ? { scale: [1, 0.9, 1] } : ''}
      className={cn('container', className, { smallOnMobile, active: animate, error: animate && !votingPossible })}
    >
      {/* PieChart is only used to handle all mouse and click events */}
      {!hide && renderTooltip && (
        <PieChart
          aria-hidden='true'
          className={cn('pie', { pointer: active && active !== 'full' })}
          startAngle={-90}
          data={pieChartData}
          onMouseOver={(_e, index) => setActive(pieChartData[index].type)}
          onClick={(_e, index) => setActive(active !== '' ? pieChartData[index].type : '')}
          onMouseOut={() => setActive('')}
        />
      )}
      {/* Visible circle that animates */}
      <svg
        aria-hidden='true'
        viewBox='0 0 36 36'
        className={cn('animatedCircle')}
        {...(remaining === 0 && { key: 'rerender-to-fix-safari-bug' })}
      >
        {paths.map(p => {
          if ((p.type === 'earned' && !earned) || (p.type === 'remaining' && remaining === 0)) {
            return null
          }
          return (
            <React.Fragment key={`path-${p.type}`}>
              <filter id='glow'>
                <feGaussianBlur stdDeviation='1' result='coloredBlur' />
                <feMerge>
                  <feMergeNode in='coloredBlur' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
              <motion.path
                animate={{ strokeDasharray: p.stroke, opacity: 1 }}
                transition={{ strokeDasharray: { type: 'spring', stiffness: 100 } }}
                initial={p.initial}
                className={cn(p.type, { active: p.type === active })}
                d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
                {...(active === p.type &&
                  p.type !== 'full' && {
                    filter: 'url(#glow)',
                  })}
              />
            </React.Fragment>
          )
        })}
      </svg>

      {/* Text inside Counter */}
      <CircularCaption className={cn('title')} as='p'>
        {title}
      </CircularCaption>

      <AnimatePresence exitBeforeEnter>
        {!votingPossible ? (
          <motion.div
            onAnimationComplete={() => triggerVotesAnimation({ animate: false })}
            animate={animate ? { scale: [1, 1.3, 1] } : ''}
          >
            <MonumentHeading2
              as='p'
              className={cn('votesText', { hide })}
              aria-label={`${remaining} ${copy.available_votes?.toLowerCase()}`}
              aria-live='polite'
              role='status'
            >
              {count}
            </MonumentHeading2>
          </motion.div>
        ) : (
          <MonumentHeading2 as='p' className={cn('votesText', { hide })}>
            {parts.map((p, index) => (
              <motion.span
                key={`${p}-${index}`}
                onAnimationComplete={() => triggerVotesAnimation({ animate: false })}
                initial={{ y: '-30%', opacity: 0.5 }}
                animate={{ y: '0%', opacity: 1 }}
                transition={{ duration: 1.4, type: 'spring' }}
              >
                {p}
              </motion.span>
            ))}
          </MonumentHeading2>
        )}
      </AnimatePresence>

      {/* Tooltip */}
      {renderTooltip && (
        <AnimatePresence>
          {active && activeTooltip && (
            <div key={`active-${active}`} className={cn('tooltip')}>
              <TooltipBox variants={tooltipVariants} custom={isDesktop} className={cn('content', { [active]: true })}>
                {activeTooltip}
              </TooltipBox>
            </div>
          )}
        </AnimatePresence>
      )}
    </motion.div>
  )
}

export default Counter
